const STARTUP_ERROR_ELEMENT_ID = 'eproc-startup-error';

/**
 * Renders a static error panel when the application fails to initialize.
 *
 * At that point Angular has not bootstrapped and i18n is not available, so no component and
 * no translation can be used and the wording stays hardcoded and bilingual. Without this the
 * user is left with a blank page and no indication of what went wrong - the failure mode that
 * made the iframe misconfiguration in EPROC-11535 / EPROC-11508 so expensive to diagnose.
 *
 * The detail line is rendered via textContent because it can carry values supplied by the
 * embedding parent window (for example a rejected API base).
 */
export function renderStartupError(error: unknown): void {
    if (typeof document === 'undefined' || !document.body) {
        return;
    }

    if (document.getElementById(STARTUP_ERROR_ELEMENT_ID)) {
        return;
    }

    const panel = document.createElement('div');
    panel.id = STARTUP_ERROR_ELEMENT_ID;
    panel.setAttribute('role', 'alert');
    panel.style.cssText = [
        'margin:2rem auto',
        'max-width:34rem',
        'padding:1.25rem 1.5rem',
        'border:1px solid #d6d8db',
        'border-radius:4px',
        'background:#fff',
        'color:#212529',
        'font-family:sans-serif',
        'line-height:1.45',
    ].join(';');

    panel.appendChild(createHeading('Die Anwendung konnte nicht gestartet werden'));
    panel.appendChild(
        createParagraph(
            'Bitte laden Sie die Seite neu. Besteht das Problem weiterhin, wenden Sie sich mit dem ' +
                'unten angezeigten Hinweis an Ihre Administration.',
        ),
    );
    panel.appendChild(
        createParagraph(
            'The application could not be started. Please reload the page and contact your ' +
                'administrator with the detail shown below if the problem persists.',
        ),
    );
    panel.appendChild(createDetail(describeError(error)));

    document.body.appendChild(panel);
}

function createHeading(text: string): HTMLElement {
    const heading = document.createElement('h2');
    heading.textContent = text;
    heading.style.cssText = 'margin:0 0 .75rem;font-size:1.15rem';

    return heading;
}

function createParagraph(text: string): HTMLElement {
    const paragraph = document.createElement('p');
    paragraph.textContent = text;
    paragraph.style.cssText = 'margin:0 0 .75rem';

    return paragraph;
}

function createDetail(text: string): HTMLElement {
    const detail = document.createElement('p');
    detail.textContent = text;
    detail.style.cssText =
        'margin:0;font-family:monospace;font-size:.85rem;color:#495057;overflow-wrap:anywhere';

    return detail;
}

function describeError(error: unknown): string {
    const message = readMessage(error);
    if (message) {
        return message;
    }

    if (typeof error === 'string' && error.trim()) {
        return error;
    }

    return 'Unknown initialization error';
}

/**
 * Duck-types on `message` instead of testing `instanceof Error`: an `HttpErrorResponse` implements
 * Error but does not inherit from it, and it reaches this code whenever the API fails during
 * bootstrap (the version and environment calls run inside the error boundary).
 */
function readMessage(error: unknown): string | null {
    if (typeof error !== 'object' || error === null || !('message' in error)) {
        return null;
    }

    const message = (error as { readonly message: unknown }).message;

    return typeof message === 'string' && message.trim() ? message : null;
}
